import { memo, useMemo, useState } from "react";
import styled from "styled-components";
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from "../PanelStyle";
import { FaXmark } from "react-icons/fa6";
import {
  PiKeyboardBold,
  PiMagnifyingGlassBold,
  PiPlayFill,
} from "react-icons/pi";
import { useAtom } from 'jotai';
import { showNavigatorPanelAtom } from "../../stores/mapStore";
import { Button, Input, Popover } from "antd";
import keyboardShortcutGuide from './keyboard_shortcut_guide.png';
import {
  filterShortcutItems,
  KEYBOARD_SHORTCUT_ITEMS,
  ShortcutActionType,
  ShortcutCategory,
} from "../../utils/keyboardShortcutUtils";

const CATEGORY_LABELS: Record<ShortcutCategory, string> = {
  camera: 'Camera & Góc nhìn bản đồ',
  drawing: 'Công cụ Vẽ đối tượng (Element)',
  panels: 'Bật / Tắt Bảng điều khiển',
  general: 'Hệ thống & Trợ giúp',
};

export const NavigatorPanel = () => {
  const [showNavigatorPanel, setShowNavigatorPanel] = useAtom(showNavigatorPanelAtom);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'all' | ShortcutCategory>('all');
  const [tipsPopoverOpen, setTipsPopoverOpen] = useState(false);

  const categories: ShortcutCategory[] = ['camera', 'drawing', 'panels', 'general'];

  const filteredItems = useMemo(
    () => filterShortcutItems(KEYBOARD_SHORTCUT_ITEMS, searchQuery, selectedCategory),
    [searchQuery, selectedCategory],
  );

  const visibleCategories = useMemo(
    () =>
      selectedCategory === 'all'
        ? categories.filter((cat) => filteredItems.some((item) => item.category === cat))
        : [selectedCategory],
    [selectedCategory, filteredItems],
  );

  const triggerShortcutFromCheatsheet = (action: ShortcutActionType) => {
    setTipsPopoverOpen(false);
    window.dispatchEvent(
      new CustomEvent('geocloud:trigger-shortcut', {
        detail: {
          action,
          closeCheatsheet: action !== 'TOGGLE_NAVIGATOR_PANEL',
        },
      }),
    );
  };

  const shortcutTipsContent = (
    <UI_TipsPopoverContent data-testid="shortcut-tips-popover">
      <div className="tips-header">
        <PiKeyboardBold size={18} style={{ color: '#2563eb', flexShrink: 0 }} />
        <span>Mẹo sử dụng phím tắt</span>
      </div>
      <div className="tips-body">
        <p>
          • <strong>Nhấn trực tiếp phím tắt:</strong> Ví dụ <kbd>Ctrl + F</kbd> (phóng tới dữ liệu),{' '}
          <kbd>Ctrl + 0</kbd> (mặc định), <kbd>P</kbd> / <kbd>L</kbd> / <kbd>R</kbd> (vẽ Điểm / Đường / Vùng),{' '}
          <kbd>Alt + 1..7</kbd> (bật/tắt bảng).
        </p>
        <p>
          • <strong>Chế độ chuỗi lệnh (Command Chord):</strong>{' '}
          <span>Hãy giữ Ctrl + K sau đó giữ Ctrl + (lệnh bạn muốn)</span> để mở thanh điều khiển nhanh trên bản đồ.
        </p>
        <p>
          • <strong>Thực thi 1 chạm:</strong> Bấm trực tiếp vào bất kỳ dòng phím tắt nào hoặc nút{' '}
          <strong>▶ Dùng</strong> để kích hoạt tính năng ngay lập tức.
        </p>
      </div>
      <img
        className="tips-guide-img"
        src={keyboardShortcutGuide}
        alt="Hướng dẫn phím tắt bàn phím"
      />
      <div className="tips-footer">
        <Button
          type="primary"
          size="small"
          data-testid="navigator-open-chord-hud-btn"
          onClick={() => triggerShortcutFromCheatsheet('TOGGLE_CHORD')}
        >
          Mở thanh lệnh nhanh (Ctrl + K)
        </Button>
      </div>
    </UI_TipsPopoverContent>
  );

  return (
    <UI_Wrapper
      data-testid="navigator-panel"
      onWheel={(e) => e.stopPropagation()}
    >
      <UI_Header style={{ flexShrink: 0 }}>
        <UI_HeaderLeft style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PiKeyboardBold style={{ color: '#2563eb', fontSize: '18px' }} />
          <span>Bảng phím tắt (Shortcut Cheatsheet)</span>
        </UI_HeaderLeft>
        <UI_HeaderRight>
          <Button
            size="small"
            type="text"
            style={{ color: '#2563eb', fontWeight: 600, fontSize: 12 }}
            onClick={() => triggerShortcutFromCheatsheet('TOGGLE_CHORD')}
          >
            Ctrl + K
          </Button>
          <UI_HeaderRight_item
            data-testid="navigator-close-btn"
            onClick={() => setShowNavigatorPanel(!showNavigatorPanel)}
            title="Đóng (Esc)"
          >
            <FaXmark />
          </UI_HeaderRight_item>
        </UI_HeaderRight>
      </UI_Header>

      {/* Thanh công cụ cố định (Sticky Toolbar): Tiêu đề + Nút chữ (i) + Tìm kiếm + Bộ lọc nhóm */}
      <UI_StickyToolbar>
        <UI_ToolbarRow>
          <UI_TitleGroup>
            <UI_SectionTitle>
              Bảng Phím tắt Điều khiển Bản đồ ({filteredItems.length}/{KEYBOARD_SHORTCUT_ITEMS.length})
            </UI_SectionTitle>
            <Popover
              content={shortcutTipsContent}
              trigger={['click', 'hover']}
              placement="bottomLeft"
              open={tipsPopoverOpen}
              onOpenChange={setTipsPopoverOpen}
              overlayStyle={{ zIndex: 10050, maxWidth: 480 }}
            >
              <UI_InfoBtn
                type="button"
                data-testid="shortcut-tips-info-btn"
                aria-label="Mẹo sử dụng phím tắt"
                title="Mẹo sử dụng phím tắt"
              >
                <span className="info-badge">i</span>
              </UI_InfoBtn>
            </Popover>
          </UI_TitleGroup>

          <Input
            allowClear
            prefix={<PiMagnifyingGlassBold style={{ color: '#64748b' }} />}
            placeholder="Tìm kiếm phím tắt (ví dụ: zoom, vẽ vùng, Ctrl + F, Alt + 2)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="shortcut-cheatsheet-search"
            style={{ maxWidth: 360 }}
          />
        </UI_ToolbarRow>

        <UI_FilterTabs data-testid="shortcut-cheatsheet-tabs">
          <UI_FilterTab
            type="button"
            $active={selectedCategory === 'all'}
            data-testid="cheatsheet-category-all"
            onClick={() => setSelectedCategory('all')}
          >
            Tất cả ({KEYBOARD_SHORTCUT_ITEMS.length})
          </UI_FilterTab>
          {categories.map((cat) => {
            const count = KEYBOARD_SHORTCUT_ITEMS.filter((i) => i.category === cat).length;
            return (
              <UI_FilterTab
                key={cat}
                type="button"
                $active={selectedCategory === cat}
                data-testid={`cheatsheet-category-${cat}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {CATEGORY_LABELS[cat]} ({count})
              </UI_FilterTab>
            );
          })}
        </UI_FilterTabs>
      </UI_StickyToolbar>

      {/* Vùng nội dung cuộn dọc mượt mà (Scrollable Body) */}
      <UI_Content data-testid="navigator-scroll-body">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Bảng tổng hợp phím tắt */}
          <UI_ShortcutSection data-testid="navigator-shortcut-table">
            {filteredItems.length === 0 ? (
              <UI_EmptyState data-testid="shortcut-cheatsheet-empty">
                Không tìm thấy phím tắt nào khớp với từ khóa &ldquo;{searchQuery}&rdquo;.
              </UI_EmptyState>
            ) : (
              <UI_CategoryGrid>
                {visibleCategories.map((cat) => {
                  const items = filteredItems.filter((i) => i.category === cat);
                  if (items.length === 0) return null;
                  return (
                    <UI_CategoryCard key={cat} data-testid={`cheatsheet-card-${cat}`}>
                      <UI_CategoryHeader>{CATEGORY_LABELS[cat]}</UI_CategoryHeader>
                      <UI_ShortcutList>
                        {items.map((item) => (
                          <UI_ShortcutRow
                            key={item.id}
                            data-testid={`shortcut-row-${item.id}`}
                            onClick={() => triggerShortcutFromCheatsheet(item.id)}
                            title={`Bấm để thực thi: ${item.label} (${item.keys})`}
                          >
                            <UI_ShortcutInfo>
                              <div className="shortcut-label">{item.label}</div>
                              <div className="shortcut-desc">{item.description}</div>
                            </UI_ShortcutInfo>
                            <UI_ShortcutRight>
                              <UI_ShortcutKeys>
                                <kbd>{item.keys}</kbd>
                              </UI_ShortcutKeys>
                              <UI_TriggerBtn
                                type="button"
                                data-testid={`shortcut-trigger-${item.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  triggerShortcutFromCheatsheet(item.id);
                                }}
                              >
                                <PiPlayFill size={10} />
                                <span>Dùng</span>
                              </UI_TriggerBtn>
                            </UI_ShortcutRight>
                          </UI_ShortcutRow>
                        ))}
                      </UI_ShortcutList>
                    </UI_CategoryCard>
                  );
                })}
              </UI_CategoryGrid>
            )}
          </UI_ShortcutSection>
        </div>
      </UI_Content>
    </UI_Wrapper>
  );
};

export default memo(NavigatorPanel);

const UI_Wrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 82vw;
  max-width: 1060px;
  height: 62vh;
  max-height: 520px;
  top: 50%;
  left: 50%;
  border-radius: 16px;
  transform: translate(-50%, -50%);
  background-color: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.24);
  border: 1px solid #e2e8f0;
  z-index: 9999;
  overflow: hidden;
`;

const UI_StickyToolbar = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 20px 12px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
`;

const UI_TitleGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const UI_InfoBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
  cursor: pointer;
  transition: all 0.15s ease;

  .info-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: Georgia, serif;
    font-style: italic;
    font-weight: 700;
    font-size: 13px;
    line-height: 1;
    color: #2563eb;
  }

  &:hover {
    background: #2563eb;
    border-color: #2563eb;
    .info-badge {
      color: #ffffff;
    }
  }
`;

const UI_TipsPopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 440px;
  color: #1e293b;
  font-size: 12.5px;
  line-height: 1.55;

  .tips-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    padding-bottom: 6px;
    border-bottom: 1px solid #e2e8f0;
  }

  .tips-body {
    display: flex;
    flex-direction: column;
    gap: 6px;

    p {
      margin: 0;
    }

    kbd {
      display: inline-block;
      padding: 1px 6px;
      border-radius: 4px;
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      font-family: monospace;
      font-size: 11px;
      font-weight: 600;
      color: #1d4ed8;
    }
  }

  .tips-guide-img {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }

  .tips-footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 4px;
  }
`;

const UI_Content = styled.div`
  flex: 1;
  min-height: 0;
  padding: 16px 20px 24px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 #f1f5f9;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #94a3b8;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #64748b;
  }
`;

const UI_ShortcutSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UI_SectionTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
`;

const UI_ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const UI_FilterTabs = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const UI_FilterTab = styled.button<{ $active?: boolean }>`
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid ${({ $active }) => ($active ? '#2563eb' : '#cbd5e1')};
  background: ${({ $active }) => ($active ? '#2563eb' : '#ffffff')};
  color: ${({ $active }) => ($active ? '#ffffff' : '#334155')};

  &:hover {
    border-color: #2563eb;
    color: ${({ $active }) => ($active ? '#ffffff' : '#1d4ed8')};
  }
`;

const UI_EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
`;

const UI_CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 14px;
`;

const UI_CategoryCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
`;

const UI_CategoryHeader = styled.div`
  padding: 8px 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #334155;
`;

const UI_ShortcutList = styled.div`
  display: flex;
  flex-direction: column;
`;

const UI_ShortcutRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const UI_ShortcutRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const UI_TriggerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #2563eb;
    border-color: #2563eb;
    color: #ffffff;
  }
`;

const UI_ShortcutInfo = styled.div`
  flex: 1;
  min-width: 0;

  .shortcut-label {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
  }

  .shortcut-desc {
    font-size: 11.5px;
    color: #64748b;
    margin-top: 2px;
  }
`;

const UI_ShortcutKeys = styled.div`
  flex-shrink: 0;

  kbd {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 6px;
    background: #f8fafc;
    border: 1px solid #cbd5e1;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    font-family: monospace;
    font-size: 11.5px;
    font-weight: 600;
    color: #0f172a;
  }
`;

const UI_Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UI_Group_Header = styled.div`
  font-weight: 600;
  color: #1e293b;
`;

const UI_Group_Content = styled.div`
  color: #475569;
  padding: 8px 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;

  ul {
    margin: 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;
